/* path: frontend/src/components/ThemeSwitch.tsx */
import { useEffect, useState } from 'react';
import { followSystem, setDarkMode, isDarkMode, reflectDataTheme } from '../lib/darkMode';

type Mode = 'system' | 'light' | 'dark';

function readPref(): Mode {
  try {
    const v = localStorage.getItem('prefers-dark');
    if (v === 'true') return 'dark';
    if (v === 'false') return 'light';
    return 'system';
  } catch {
    return 'system';
  }
}

export default function ThemeSwitch() {
  const [mode, setMode] = useState<Mode>('system');
  const [effectiveDark, setEffectiveDark] = useState<boolean>(false);

  useEffect(() => {
    setMode(readPref());
    setEffectiveDark(isDarkMode());
  }, []);

  useEffect(() => {
    // why: expose data-theme for libs/components that look it up
    reflectDataTheme();
  }, [mode]);

  const apply = (m: Mode) => {
    setMode(m);
    if (m === 'system') followSystem();
    else setDarkMode(m === 'dark');
    setEffectiveDark(isDarkMode());
  };

  const Item = ({ m, label }: { m: Mode; label: string }) => (
    <button
      type="button"
      onClick={() => apply(m)}
      aria-pressed={mode === m}
      className={[
        'px-3 py-1.5 text-sm rounded-lg transition-colors',
        mode === m
          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
          : 'text-slate-700 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/10',
      ].join(' ')}
    >
      {label}
    </button>
  );

  return (
    <div className="inline-flex items-center gap-2 rounded-2xl border border-black/10 dark:border-white/10 p-1.5">
      <Item m="system" label="System" />
      <Item m="light" label="Light" />
      <Item m="dark" label="Dark" />
    </div>
  );
}
